/**
 * Form Values Management Hook
 * Handles form values filtering, transformation, and change tracking
 */
import { useCallback, useMemo, useRef, useEffect } from 'react'
import { FormFieldConfig, FormChange } from '../types'
import { transformFieldValue } from '../utils/dynamicFields'
import { getFormChanges } from '../utils/changeTracker'

interface UseFormValuesProps {
  fields: FormFieldConfig[]
  initialValues: Record<string, any>
  allValues: Record<string, any>
  onChange?: (values: Record<string, any>, changes: FormChange[]) => void
}

interface UseFormValuesReturn {
  configuredFieldNames: Set<string>
  filterConfiguredValues: (values: Record<string, any>) => Record<string, any>
  applyAllTransforms: (values: Record<string, any>) => Record<string, any>
  getFormattedValues: () => Record<string, any>
  getFormChanges: () => FormChange[]
  handleValueChange: (
    changedField: string,
    currentValues: Record<string, any>
  ) => void
  resetPreviousValues: (values: Record<string, any>) => void
}

export const useFormValues = ({
  fields,
  initialValues,
  allValues,
  onChange
}: UseFormValuesProps): UseFormValuesReturn => {
  // Keep track of previous values for change detection
  const prevAllValuesRef = useRef<Record<string, any>>({})

  // Store original initial values for change tracking
  const originalValues = useMemo(() => initialValues, [])

  /**
   * Get field names defined in the form configuration
   */
  const configuredFieldNames = useMemo(() => {
    return new Set(fields.map(field => field.name))
  }, [fields])

  /**
   * Filter values to only include fields defined in the form configuration
   */
  const filterConfiguredValues = useCallback(
    (values: Record<string, any>): Record<string, any> => {
      const filteredValues: Record<string, any> = {}

      Object.keys(values).forEach(key => {
        if (configuredFieldNames.has(key)) {
          filteredValues[key] = values[key]
        }
      })

      return filteredValues
    },
    [configuredFieldNames]
  )

  /**
   * Apply all field transforms to form values
   */
  const applyAllTransforms = useCallback(
    (values: Record<string, any>): Record<string, any> => {
      const transformedValues = { ...values }

      fields.forEach(field => {
        if (field.transform && field.name in values) {
          transformedValues[field.name] = transformFieldValue(
            field,
            values[field.name],
            values
          )
        }
      })

      return transformedValues
    },
    [fields]
  )

  /**
   * Get formatted values (filtered and transformed)
   */
  const getFormattedValues = useCallback((): Record<string, any> => {
    const filteredValues = filterConfiguredValues(allValues)
    return applyAllTransforms(filteredValues)
  }, [allValues, filterConfiguredValues, applyAllTransforms])

  /**
   * Get form changes compared to initial values
   */
  const getFormChangesFromInitial = useCallback((): FormChange[] => {
    const formattedValues = getFormattedValues()
    return getFormChanges(originalValues, formattedValues)
  }, [originalValues, getFormattedValues])

  /**
   * Handle value changes and trigger onChange callback
   */
  const handleValueChange = useCallback(
    (changedField: string, currentValues: Record<string, any>) => {
      const previousValues = prevAllValuesRef.current

      // Check if the field actually changed
      if (previousValues[changedField] !== currentValues[changedField]) {
        // Update previous values reference
        prevAllValuesRef.current = { ...currentValues }

        // Call onChange callback if provided
        if (onChange) {
          const filteredValues = filterConfiguredValues(currentValues)
          const transformedValues = applyAllTransforms(filteredValues)
          const changes = getFormChanges(originalValues, transformedValues)
          onChange(transformedValues, changes)
        }
      }
    },
    [onChange, filterConfiguredValues, applyAllTransforms, originalValues]
  )

  /**
   * Re-seed the change-detection baseline.
   *
   * `prevAllValuesRef` is otherwise only written from inside handleValueChange,
   * which never runs for a whole-form reset (react-hook-form dispatches it with
   * `name === undefined`, and the watch bridge in FormBuilder is name-gated).
   * Without this the baseline keeps the pre-reset values and the first
   * re-selection of a pre-reset value is silently swallowed.
   */
  const resetPreviousValues = useCallback((values: Record<string, any>) => {
    prevAllValuesRef.current = { ...values }
  }, [])

  /**
   * Initialize previous values reference
   */
  useEffect(() => {
    prevAllValuesRef.current = { ...allValues }
  }, [])

  return {
    configuredFieldNames,
    filterConfiguredValues,
    applyAllTransforms,
    getFormattedValues,
    getFormChanges: getFormChangesFromInitial,
    handleValueChange,
    resetPreviousValues
  }
}
